// Lazy reader migration for legacy AI Artifact `sources` shapes.
//
// The on-disk schema used to be a discriminated union:
//   { scope: 'activity'; stepIds: string[] } | { scope: 'module' }
//
// It is now flat:
//   { stepIds: string[] }
//
// When the admin opens a program, this helper walks the tree and rewrites any
// ai-artifact blocks still on the old shape. Once re-saved, they land in the
// new shape on disk. Safe to remove once every authored program has been
// touched at least once.

import type { Program } from '@app/types';

interface LegacyArtifactSources {
  scope?: 'activity' | 'module';
  stepIds?: string[];
}

export function migrateLegacyArtifactSources(program: Program): Program {
  return {
    ...program,
    modules: program.modules.map((moduleDef) => ({
      ...moduleDef,
      steps: moduleDef.steps.map((step) => ({
        ...step,
        blocks: step.blocks.map((block) => {
          if (block.type !== 'ai-artifact') return block;
          const sources = block.sources as LegacyArtifactSources & { stepIds?: string[] };
          // Already migrated — flat shape has no scope field.
          if (sources && !('scope' in sources) && Array.isArray(sources.stepIds)) return block;
          if (sources?.scope === 'activity') {
            return { ...block, sources: { stepIds: sources.stepIds ?? [] } };
          }
          if (sources?.scope === 'module') {
            return { ...block, sources: { stepIds: moduleDef.steps.map((s) => s.id) } };
          }
          // Unknown / missing — coerce to empty.
          return { ...block, sources: { stepIds: [] } };
        }),
      })),
    })),
  };
}
